import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Package, Search, CheckCircle2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'zonnepanelen', label: 'Zonnepanelen' },
  { value: 'omvormers', label: 'Omvormers' },
  { value: 'batterijen', label: 'Batterijen' },
  { value: 'laadpalen', label: 'Laadpalen' },
  { value: 'montagemateriaal', label: 'Montagemateriaal' },
  { value: 'werken', label: 'Werken' },
  { value: 'elektriciteitswerken', label: 'Elektriciteitswerken' },
];

/**
 * Reusable installation product selector (identical UX as the one used in Offertes).
 *
 * Props:
 *  - open, onOpenChange
 *  - products: array of installation products (with id, name, description, category, unit_price, unit, components)
 *  - onConfirm(selectedProducts: Product[])  -- called with array of picked products
 *  - title, description  -- optional overrides
 */
const InstallationProductSelector = ({
  open,
  onOpenChange,
  products = [],
  onConfirm,
  title = 'Producten Toevoegen',
  description = 'Selecteer één of meerdere producten om toe te voegen',
}) => {
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const productsByCategory = useMemo(() => {
    return CATEGORIES.map((cat) => ({
      ...cat,
      products: products.filter((p) => p.category === cat.value),
    }));
  }, [products]);

  const reset = () => {
    setSelected([]);
    setQuery('');
    setCategory('all');
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleConfirm = () => {
    if (selected.length === 0) return;
    onConfirm(selected);
    toast.success(`${selected.length} product${selected.length > 1 ? 'en' : ''} toegevoegd`);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Search + category */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoek product op naam..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10"
              data-testid="ps-search-input"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Alle categorieën" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle categorieën</SelectItem>
              {productsByCategory.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Selection summary */}
        {selected.length > 0 && (
          <div className="flex items-center justify-between bg-primary/10 text-primary px-3 py-2 rounded-md">
            <span className="text-sm font-medium">
              {selected.length} product{selected.length > 1 ? 'en' : ''} geselecteerd
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected([])}
              className="h-7 text-xs"
            >
              Alles deselecteren
            </Button>
          </div>
        )}

        {/* Product grid */}
        <div className="overflow-y-auto max-h-[50vh] pr-2">
          {productsByCategory
            .filter((cat) => {
              if (category !== 'all' && cat.value !== category) return false;
              if (!query) return cat.products.length > 0;
              return cat.products.some(
                (p) =>
                  p.name.toLowerCase().includes(query.toLowerCase()) ||
                  (p.description && p.description.toLowerCase().includes(query.toLowerCase()))
              );
            })
            .map((cat) => {
              const filtered = query
                ? cat.products.filter(
                    (p) =>
                      p.name.toLowerCase().includes(query.toLowerCase()) ||
                      (p.description && p.description.toLowerCase().includes(query.toLowerCase()))
                  )
                : cat.products;
              if (filtered.length === 0) return null;

              const allSelected = filtered.every((p) => selected.some((sp) => sp.id === p.id));

              return (
                <div key={cat.value} className="mb-6">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                      {cat.label} ({filtered.length})
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        if (allSelected) {
                          setSelected((prev) => prev.filter((p) => !filtered.some((fp) => fp.id === p.id)));
                        } else {
                          setSelected((prev) => {
                            const existing = new Set(prev.map((p) => p.id));
                            const toAdd = filtered.filter((p) => !existing.has(p.id));
                            return [...prev, ...toAdd];
                          });
                        }
                      }}
                    >
                      {allSelected ? 'Deselecteer alles' : 'Selecteer alles'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {filtered.map((product) => {
                      const isSelected = selected.some((p) => p.id === product.id);
                      return (
                        <div
                          key={product.id}
                          onClick={() => {
                            if (isSelected) setSelected((prev) => prev.filter((p) => p.id !== product.id));
                            else setSelected((prev) => [...prev, product]);
                          }}
                          className={`p-4 border rounded-lg cursor-pointer transition-all group relative
                            ${
                              isSelected
                                ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                                : 'hover:border-primary hover:bg-primary/5'
                            }`}
                          data-testid={`ps-tile-${product.id}`}
                        >
                          <div
                            className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                              ${
                                isSelected
                                  ? 'bg-primary border-primary text-white'
                                  : 'border-muted-foreground/30 group-hover:border-primary/50'
                              }`}
                          >
                            {isSelected && <CheckCircle2 className="w-3 h-3" />}
                          </div>
                          <div className="font-medium text-sm group-hover:text-primary pr-6">
                            {product.name}
                          </div>
                          {product.components && product.components.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1.5 space-y-0.5">
                              {product.components.slice(0, 2).map((comp, idx) => (
                                <div key={idx}>• {comp.description}</div>
                              ))}
                              {product.components.length > 2 && (
                                <div className="text-muted-foreground/60">
                                  +{product.components.length - 2} meer...
                                </div>
                              )}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-3 pt-2 border-t">
                            <span className="text-sm font-semibold text-primary">
                              €{product.unit_price?.toFixed(2) ?? '0.00'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              per {product.unit || 'stuk'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          {products.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Geen producten gevonden</p>
              <p className="text-sm">Voeg eerst producten toe in het Producten menu</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Annuleren
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selected.length === 0}
            data-testid="ps-confirm-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Voeg {selected.length > 0 ? `${selected.length} product${selected.length > 1 ? 'en' : ''}` : 'geselecteerde'} toe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InstallationProductSelector;
